import React from 'react';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';

import AutosuggestInput from 'components/autosuggestInput';
import { withTracking } from 'hooks';
import {
  buildCitySubpageUrl,
  buildCMPPageUrl,
  buildCmpSubpagesUrls,
  buildCompanyCitySalaryUrl,
  buildCompanyJobsCityUrl,
  buildCompanyJobsJobTitleUrl,
  buildCompanyJobTitleSalaryUrl,
  buildCompanyStateSalaryUrl,
  buildCppSubpageUrl,
  buildMajorPageUrl,
  buildUrlUsingTitleAndLocation,
} from 'utils';
import {
  CITY_SUBPAGES,
  CLUSTER_HEADER_CATEGORY,
  CMP_SUBPAGES,
  CPP_SUBPAGES,
  MAJOR_SUBPAGES,
  TRACKING,
} from 'utils/constant';

const propTypes = {
  subPageName: PropTypes.string.isRequired,
  clusterCategory: PropTypes.string.isRequired,
  jobLocation: PropTypes.string,
  jobTitleArticle: PropTypes.string,
  className: PropTypes.string,
  criteria: PropTypes.object,
};

const defaultProps = {
  jobLocation: '',
  jobTitleArticle: '',
  className: '',
};

// dataset for handling index/noindex pages for CMP, MAJOR, CPP subpages.
const SUGGESTIONS_FILTER = {
  // CMP_SUBPAGES display flags INDEX/NOINDEX
  CAREER: {
    [CMP_SUBPAGES.JOBS]: 'indexInfoOfJobsPage',
    [CMP_SUBPAGES.SALARY]: 'indexInfoOfSalaryPage',
    [CMP_SUBPAGES.RESUME]: 'indexInfoOfResumePage',
    [CMP_SUBPAGES.SKILLS]: 'indexInfoOfSkillsPage',
    [CMP_SUBPAGES.WHAT_THEY_DO]: 'indexInfoOfWhatTheyDoPage',
    [CMP_SUBPAGES.HOW_TO_BECOME]: 'indexInfoOfHowToBecomePage',
    [CMP_SUBPAGES.EDUCATION]: 'indexInfoOfEducationPage',
    [CMP_SUBPAGES.BEST_STATES]: 'indexInfoOfBestStatesPage',
    [CMP_SUBPAGES.REMOTE_JOBS]: 'indexInfoOfRemoteJobsPage',
    [CMP_SUBPAGES.TRENDS]: 'indexInfoOfTrendsPage',
    [CMP_SUBPAGES.DEMOGRAPHICS]: 'indexInfoOfDemographicsPage',
    [CMP_SUBPAGES.CERTIFICATIONS]: 'indexInfoOfCertificationsPage',
    [CMP_SUBPAGES.DESCRIPTION]: 'indexInfoOfDescriptionPage',
  },
  // MAJOR_SUBPAGES diplsay flags true/false
  MAJOR: {
    [MAJOR_SUBPAGES.SALARY]: 'displayFlagOfMajorSalaryPage',
    [MAJOR_SUBPAGES.BEST_COLLEGES]: 'displayFlagOfBestCollegesPage',
  },
  // CPP_SUBPAGES display flags  true/false
  COMPANY: {
    [CPP_SUBPAGES.JOBS]: 'displayFlagOfCompanyJobsPage',
    [CPP_SUBPAGES.SALARY]: 'displayFlagOfCSP',
    [CPP_SUBPAGES.HISTORY]: 'displayFlagOfCompanyHistoryPage',
    [CPP_SUBPAGES.DEMOGRAPHICS]: 'displayFlagOfCompanyDemographicsPage',
    [CPP_SUBPAGES.EXECUTIVES]: 'displayFlagOfCompanyExecutivesPage',
    [CPP_SUBPAGES.FAQ]: 'displayFlagOfCompanyFAQPage',
  },
  // CITY_SUBPAGES display flags  true/false
  CITY: {
    [CITY_SUBPAGES.SALARY]: 'displayFlagForSalaryPage',
    [CITY_SUBPAGES.PART_TIME_JOBS]: 'displayFlagForPartTimeJobsPage',
  },
};

class SearchBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: '',
    };
  }

  updateTitleSearch = (input) => {
    const { error } = this.state;
    const { jobLocation, dynamicPageName, jobTitleArticle, tracking } = this.props;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }
    if (input.value) {
      const { original_name, article } = input.valueObj;

      const subPageName = this.handleNoIndexPages(input.valueObj);

      let url = '';
      if (subPageName === CMP_SUBPAGES.WHAT_THEY_DO) {
        url += buildCmpSubpagesUrls(original_name, '', subPageName, '', article);
      } else if (subPageName === CMP_SUBPAGES.JOBS && jobLocation) {
        url += buildUrlUsingTitleAndLocation(original_name, jobLocation, '');
      } else if (subPageName === CMP_SUBPAGES.DYNAMIC && dynamicPageName) {
        url += buildCmpSubpagesUrls(original_name, '', subPageName, dynamicPageName);
      } else if (subPageName === CMP_SUBPAGES.OVERVIEW || !subPageName) {
        url += buildCMPPageUrl(original_name);
      } else if (subPageName === CMP_SUBPAGES.HOW_TO_BECOME) {
        url += buildCmpSubpagesUrls(original_name, '', subPageName, '', article);
      } else {
        url += buildCmpSubpagesUrls(original_name, '', subPageName);
      }

      tracking.logActivity(
        {
          title: {
            originalSearch: input.value,
            normalizedTitle: input.value,
          },
        },
        TRACKING.LOG_ACTIVITY.AUTO_COMPLETE
      );

      window.location.href = url;
    }
  };

  updateCompanySearch = (input) => {
    const { error } = this.state;
    const { subPageName, dynamicPage, criteria } = this.props;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }
    if (input.valueObj.companyID) {
      let subPage = this.handleNoIndexPages(input.valueObj);
      if (dynamicPage) {
        subPage = subPage.split(' ').join('-').toLowerCase();
      }
      let url;
      if (subPageName === CPP_SUBPAGES.LOCATION_COMBO_JOBS && criteria) {
        url = buildCompanyJobsCityUrl(
          input.valueObj.zippiaCppUrl,
          criteria.city,
          criteria.stateAbbr
        );
      } else if (subPageName === CPP_SUBPAGES.JOB_TITLE_COMBO_JOBS && criteria) {
        url = buildCompanyJobsJobTitleUrl(input.valueObj.zippiaCppUrl, criteria.title);
      } else if (subPageName === CPP_SUBPAGES.JOB_TITLE_SALARY) {
        url = buildCompanyJobTitleSalaryUrl(input.valueObj.zippiaCppUrl, criteria.title);
      } else if (subPageName === CPP_SUBPAGES.STATE_SALARY) {
        url = buildCompanyStateSalaryUrl(input.valueObj.zippiaCppUrl, criteria.state);
      } else if (subPageName === CPP_SUBPAGES.CITY_SALARY) {
        url = buildCompanyCitySalaryUrl(
          input.valueObj.zippiaCppUrl,
          criteria.city,
          criteria.stateAbbr
        );
      } else {
        url = buildCppSubpageUrl(input.valueObj.zippiaCppUrl, '', subPage);
        if (subPageName === CPP_SUBPAGES.JOBS) {
          localStorage.removeItem('cmpQuery');
        }
      }
      window.location.href = url;
    }
  };

  updateMajorSearch = (input) => {
    const { error } = this.state;
    const { dynamicPage, subPageName } = this.props;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }

    if (input.value) {
      const majorName = input.valueObj ? input.valueObj.original_name : '';
      const originalUrl = buildMajorPageUrl(majorName).toLowerCase();

      let subPageRoute = this.handleNoIndexPages(input.valueObj);

      if (dynamicPage) {
        subPageRoute = subPageName.split(' ').join('-').toLowerCase();
      }

      const url = subPageRoute ? `${originalUrl}${subPageRoute}/` : originalUrl;

      window.location.href = url;
    }
  };

  updateCitySearch = (input) => {
    const { error } = this.state;
    const { subPageName } = this.props;
    if (error && input.isListOpen) {
      this.setState({
        error: '',
      });
    }

    if (input.valueObj && Object.keys(input.valueObj).length > 0) {
      const { city, state } = input.valueObj;

      const subPageRoute = this.handleNoIndexPages(input.valueObj);

      const url = buildCitySubpageUrl({ city, stateAbbr: state }, subPageRoute);
      window.location.href = url;
    }
  };

  /**
   * @param valueObj -> value object from autosuggestion input
   * @returns {subPageName} -> current subPageName or empty string which means Overview page;
   */
  handleNoIndexPages = (valueObj) => {
    const { clusterCategory, subPageName } = this.props;

    switch (clusterCategory) {
      case CLUSTER_HEADER_CATEGORY.CAREER:
        return SUGGESTIONS_FILTER.CAREER[subPageName] &&
          valueObj.title_indexinfo &&
          valueObj.title_indexinfo[SUGGESTIONS_FILTER.CAREER[subPageName]] === 'NOINDEX'
          ? ''
          : subPageName;
      case CLUSTER_HEADER_CATEGORY.MAJOR:
        return SUGGESTIONS_FILTER.MAJOR[subPageName] &&
          valueObj.major_indexinfo &&
          valueObj.major_indexinfo[SUGGESTIONS_FILTER.MAJOR[subPageName]]
          ? subPageName
          : '';
      case CLUSTER_HEADER_CATEGORY.COMPANY:
        return SUGGESTIONS_FILTER.COMPANY[subPageName] &&
          !valueObj[SUGGESTIONS_FILTER.COMPANY[subPageName]]
          ? 'overview'
          : subPageName;
      case CLUSTER_HEADER_CATEGORY.CITY:
        return SUGGESTIONS_FILTER.CITY[subPageName] &&
          valueObj?.[SUGGESTIONS_FILTER.CITY[subPageName]]
          ? subPageName
          : '';
    }
  };

  handleAutosuggestError = (errorObject) => {
    this.setState({
      error: errorObject.errorMessage,
    });
  };

  render() {
    const { error } = this.state;
    const { clusterCategory, className } = this.props;

    return (
      <div className={classNames('cluster-header-search-bar-container', className)}>
        {clusterCategory === CLUSTER_HEADER_CATEGORY.CAREER && (
          <AutosuggestInput
            category="jobTitle"
            inputProps={{
              className: 'typeahead form-control filterInput',
              name: 'jobTitle',
              placeholder: 'Search For A Job Title',
            }}
            onChange={this.updateTitleSearch}
            onErrorCallback={this.handleAutosuggestError}
            isIndexableOnly
          />
        )}

        {clusterCategory === CLUSTER_HEADER_CATEGORY.COMPANY && (
          <AutosuggestInput
            category="company"
            inputProps={{
              className: 'typeahead form-control filterInput',
              name: 'company',
              placeholder: 'Search For A Company',
            }}
            onChange={this.updateCompanySearch}
            onErrorCallback={this.handleAutosuggestError}
            isIndexableOnly
          />
        )}

        {clusterCategory === CLUSTER_HEADER_CATEGORY.MAJOR && (
          <AutosuggestInput
            category="major"
            inputProps={{
              className: 'typeahead form-control filterInput',
              name: 'major',
              placeholder: 'Search For A Major',
            }}
            onChange={this.updateMajorSearch}
            onErrorCallback={this.handleAutosuggestError}
            isIndexableOnly
          />
        )}

        {clusterCategory === CLUSTER_HEADER_CATEGORY.CITY && (
          <AutosuggestInput
            category="location"
            inputProps={{
              name: 'city',
              className: 'typeahead form-control filterInput',
              placeholder: 'Search For A City',
              id: 'cityClusterSearchField',
            }}
            onChange={this.updateCitySearch}
            onErrorCallback={this.handleAutosuggestError}
            cityOnlyLocations={true}
          />
        )}

        {error && <p className="error-message">{error}</p>}
      </div>
    );
  }
}

SearchBar.propTypes = propTypes;
SearchBar.defaultProps = defaultProps;

export default withTracking(SearchBar);
